import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import SharedFilters from '../../../components/SharedFilters'
import AppActionMenu from '../../../components/AppActionMenu'
import AppPagination from '../../../components/AppPagination'
import { appToast } from '../../../components/AppToast'
import { resolveMediaUrl } from '../../../utils/mediaUrl'
import { useStudentsListScreen } from './hooks/useStudentsListScreen'
import { useExportJob } from '../../../hooks/useExportJob'
import styles from './StudentsListScreen.module.css'
import SendSMSModal from './components/modals/SendSMSModal'
import SendEmailModal from './components/modals/SendEmailModal'
import SendReportCardModal from './components/modals/SendReportCardModal'
import BatchTransferModal from './components/modals/BatchTransferModal'
import RegenerateRollNoModal from './components/modals/RegenerateRollNoModal'
import UpdateColumnsModal from './components/modals/UpdateColumnsModal'
import PrintReportModal from './components/modals/PrintReportModal'

// ─── Avatar ────────────────────────────────────────────────────────────────────
function StudentAvatar({ student }) {
  const photoSrc = resolveMediaUrl(student.photoUrl)
  if (photoSrc) {
    return (
      <img
        src={photoSrc}
        alt={`${student.firstName} ${student.lastName}`}
        className="h-11 w-11 rounded-full object-cover ring-2 ring-primary/10"
      />
    )
  }
  const initials = `${(student.firstName || '?')[0]}${(student.lastName || '')[0] || ''}`.toUpperCase()
  return (
    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-secondary-container text-sm font-bold text-primary">
      {initials}
    </div>
  )
}

// ─── Main Component ────────────────────────────────────────────────────────────
export default function StudentsListScreen() {
  const navigate = useNavigate()
  const { state, actions } = useStudentsListScreen()
  const [activeTab, setActiveTab] = useState('Active') // Active | Archive | All

  // Modal state
  const [activeModal, setActiveModal] = useState(null)
  const openModal = (type) => setActiveModal(type)
  const closeModal = () => setActiveModal(null)

  // Print Report modal
  const [showPrintReport, setShowPrintReport] = useState(false)

  // Row selection state (for batch actions)
  const [selectedRowIds, setSelectedRowIds] = useState(() => new Set())
  const toggleRow = (id) => {
    setSelectedRowIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }
  const toggleAllRows = () => {
    if (selectedRowIds.size === state.rows.length) {
      setSelectedRowIds(new Set())
    } else {
      setSelectedRowIds(new Set(state.rows.map((r) => r.id)))
    }
  }


  // Sync tab → status filter
  const handleTabClick = (tab) => {
    setActiveTab(tab)
    actions.setStatus(tab === 'All' ? 'All' : tab === 'Archive' ? 'Inactive' : 'Active')
  }

  // Map option arrays to {value, label} for SharedFilters
  const toOptions = (arr) =>
    arr.map((item) =>
      typeof item === 'object' ? item : { value: item, label: item },
    )

  const { triggerExport, isExporting } = useExportJob({ module: 'students' })

  // Download handler (Universal Background Job - Rule 89)
  const handleDownload = () => {
    triggerExport({
      searchField: state.searchField,
      searchText: state.searchText,
      status: activeTab === 'All' ? '' : activeTab === 'Archive' ? 'Inactive' : 'Active',
      branch: state.branch === 'All' ? '' : state.branch,
      course: state.course === 'All' ? '' : state.course,
      batch: state.batch === 'All' ? '' : state.batch,
      scheme: state.scheme === 'All' ? '' : state.scheme,
      gender: state.gender === 'All' ? '' : state.gender,
      fromDate: state.fromDate,
      toDate: state.toDate,
    })
  }

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-12">

      {/* ── Page Header ───────────────────────────────────────────────────── */}
      <section className="flex flex-col gap-5 border-b border-outline-variant/10 pb-5 xl:flex-row xl:items-end xl:justify-between">
        <div className="max-w-4xl">
          <p className="text-xs font-black uppercase tracking-[0.18em] text-primary">Admission Module</p>
          <h1 className="mt-2 text-4xl font-extrabold leading-[0.98] tracking-[-0.02em] text-on-surface">Students</h1>
          <p className="mt-2 text-base leading-relaxed text-on-surface-variant">
            Search, filter and manage student admissions, enrollment status and fee records.
          </p>
        </div>

        {/* Active / Archive / All tabs — segmented control matching Master pattern */}
        <div className="flex items-center gap-2 rounded-xl bg-surface-container-high p-1">
          {['Active', 'Archive', 'All'].map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => handleTabClick(tab)}
              className={`rounded-lg px-4 py-2 text-sm font-bold transition-colors ${
                activeTab === tab
                  ? 'bg-surface-container-lowest text-primary shadow-sm'
                  : 'text-on-surface-variant hover:text-on-surface'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </section>

      {/* ── Filters ────────────────────────────────────────────────────────── */}
      <SharedFilters
        // Search
        searchField={state.searchField}
        searchText={state.searchText}
        setSearchField={actions.setSearchField}
        setSearchText={actions.setSearchText}
        searchFieldOptions={[
          { value: 'Name', label: 'Name' },
          { value: 'Mobile', label: 'Mobile' },
          { value: 'Email', label: 'Email' },
          { value: 'Roll No.', label: 'Roll No.' },
          { value: 'ID No.', label: 'ID No.' },
          { value: 'Address', label: 'Address' },
          { value: 'City', label: 'City' },
          { value: 'Guardian', label: 'Guardian' },
          { value: 'BirthPlace', label: 'Birth Place' },
          { value: 'Caste', label: 'Caste' },
        ]}
        // Status (shown inline, not in accordion)
        showStatus={false}
        showSearchButtons={true}
        // Accordion filters — Education reference filters
        showAccordionToggle={true}
        visibleAccordionFilters={[
          'company', 'branch', 'course', 'batch', 'batch2',
          'scheme', 'academicYear', 'datePeriod', 'fromDate', 'toDate',
          'subject', 'subject2', 'counsellor', 'gender',
        ]}
        companyFilter={state.company}
        branchFilter={state.branch}
        courseFilter={state.course}
        batchFilter={state.batch}
        batch2Filter={state.batch2}
        schemeFilter={state.scheme}
        academicYearFilter={state.academicYear}
        datePeriodFilter={state.datePeriod}
        fromDate={state.fromDate}
        toDate={state.toDate}
        subjectFilter={state.subject}
        subject2Filter={state.subject2}
        counsellorFilter={state.counsellor}
        genderFilter={state.gender}
        setCompanyFilter={actions.setCompany}
        setBranchFilter={actions.setBranch}
        setCourseFilter={actions.setCourse}
        setBatchFilter={actions.setBatch}
        setBatch2Filter={actions.setBatch2}
        setSchemeFilter={actions.setScheme}
        setAcademicYearFilter={actions.setAcademicYear}
        setDatePeriodFilter={actions.setDatePeriod}
        setFromDate={actions.setFromDate}
        setToDate={actions.setToDate}
        setSubjectFilter={actions.setSubject}
        setSubject2Filter={actions.setSubject2}
        setCounsellorFilter={actions.setCounsellor}
        setGenderFilter={actions.setGender}
        companyOptions={toOptions(state.options.companyOptions)}
        branchOptions={toOptions(state.options.branchOptions)}
        courseOptions={toOptions(state.options.courseOptions)}
        batchOptions={toOptions(state.options.batchOptions)}
        batch2Options={toOptions(state.options.batch2Options)}
        schemeOptions={toOptions(state.options.schemeOptions)}
        academicYearOptions={toOptions(state.options.academicYearOptions)}
        datePeriodOptions={toOptions(state.options.datePeriodOptions)}
        subjectOptions={toOptions(state.options.subjectOptions)}
        subject2Options={toOptions(state.options.subject2Options)}
        counsellorOptions={toOptions(state.options.counsellorOptions)}
        genderOptions={toOptions(state.options.genderOptions)}
        // Search / Reset
        onSearch={actions.refresh}
        onReset={actions.resetFilters}
        // Tools dropdown
        tools={[
          { key: 'upload', label: 'Upload Excel', icon: 'cloud_upload' },
          { key: 'send-sms', label: 'Send SMS', icon: 'sms' },
          { key: 'send-email', label: 'Send Email', icon: 'mail' },
          { key: 'batch-transfer', label: 'Batch Transfer', icon: 'swap_horiz' },
          { key: 'print-report', label: 'Print Report', icon: 'print' },
          { key: 'download', label: 'Download', icon: 'download' },
          { key: 'regenerate-roll-no', label: 'Regenerate Roll No', icon: 'refresh' },
          { key: 'update-columns', label: 'Update Columns', icon: 'view_column' },
          { key: 'send-report-card', label: 'Send Report Card Template', icon: 'send' },
        ]}
        onToolSelect={(key) => {
          if (key === 'upload') {
            navigate('/admission/upload-excel/list')
          } else if (key === 'send-sms') {
            openModal('sms')
          } else if (key === 'send-email') {
            openModal('email')
          } else if (key === 'batch-transfer') {
            openModal('batch-transfer')
          } else if (key === 'print-report') {
            setShowPrintReport(true)
          } else if (key === 'download') {
            handleDownload()
          } else if (key === 'regenerate-roll-no') {
            openModal('regenerate-roll-no')
          } else if (key === 'update-columns') {
            openModal('update-columns')
          } else if (key === 'send-report-card') {
            openModal('send-report-card')
          }
        }}
      />

      {/* ── Error ──────────────────────────────────────────────────────────── */}
      {state.error && (
        <div className="rounded-xl border border-error/30 bg-error-container px-4 py-3 text-sm text-error">
          {state.error}
        </div>
      )}

      {/* ── Student Table ─────────────────────────────────────────────────── */}
      <div className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest overflow-hidden shadow-sm flex flex-col">
        {/* Column headers */}
        <div className="grid grid-cols-[40px_minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)_60px] items-center border-b border-outline-variant/25 bg-surface-container-low/60 px-8 py-3.5 text-xs font-bold uppercase tracking-[0.08em] text-on-surface-variant select-none">
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={state.rows.length > 0 && selectedRowIds.size === state.rows.length}
              ref={(el) => { if (el) el.indeterminate = selectedRowIds.size > 0 && selectedRowIds.size < state.rows.length }}
              onChange={toggleAllRows}
              className="h-4 w-4 rounded border-outline-variant text-primary focus:ring-primary/20 cursor-pointer"
              aria-label="Select all"
            />
          </div>
          <div>Name/ Mobile/ Email</div>
          <div>RollNo/ IDCardNo/ JoiningDate</div>
          <div>Course/ Batches/ Branch</div>
          <div className="text-right">Actions</div>
        </div>

        {/* Data rows */}
        <div className="flex flex-col">
          {state.isLoading ? (
            <div className="px-6 py-16 text-center text-sm text-on-surface-variant font-medium">
              Loading students...
            </div>
          ) : state.rows.length === 0 ? (
            <div className="px-6 py-16 text-center">
              <span className="material-symbols-outlined mb-3 block text-5xl text-on-surface-variant/40">inbox</span>
              <p className="text-sm font-medium text-on-surface-variant">No students match the current filters.</p>
              <button
                type="button"
                onClick={actions.resetFilters}
                className="mt-4 rounded-xl bg-primary/10 px-5 py-2 text-sm font-bold text-primary hover:bg-primary/20"
              >
                Show All Students
              </button>
            </div>
          ) : (
            state.rows.map((row, rowIdx) => (
              <div
                key={row.id}
                className={`group grid grid-cols-[40px_minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)_60px] items-center border-b border-outline-variant/10 px-8 py-4 transition-colors hover:bg-surface-container-low/40 last:border-b-0 cursor-pointer ${
                  rowIdx % 2 === 0 ? 'bg-white' : 'bg-slate-50'
                }`}
                onClick={() => navigate(`/admission/students/entry?id=${row.id}&mode=edit`)}
              >
                {/* Checkbox */}
                <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedRowIds.has(row.id)}
                    onChange={() => toggleRow(row.id)}
                    className="h-4 w-4 rounded border-outline-variant text-primary focus:ring-primary/20 cursor-pointer"
                  />
                </div>

                {/* Column 1: Name/ Mobile/ Email */}
                <div className="flex items-center gap-4 min-w-0 pr-4">
                  <StudentAvatar student={row} />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="truncate text-base font-bold text-on-surface group-hover:text-primary transition-colors">
                        {row.firstName} {row.lastName}
                      </p>
                    </div>
                    {row.mobile && (
                      <p className="text-xs text-on-surface-variant mt-0.5">{row.mobile}</p>
                    )}
                    {row.email && (
                      <p className="text-[11px] text-on-surface-variant truncate mt-0.5">{row.email}</p>
                    )}
                  </div>
                </div>

                {/* Column 2: RollNo/ IDCardNo/ JoiningDate */}
                <div className="min-w-0 pr-4">
                  <p className="text-sm font-semibold text-on-surface leading-snug">{row.registrationNo || '—'}</p>
                  <p className="text-xs text-on-surface-variant leading-snug">{row.studentCode || '—'}</p>
                  <p className="text-xs text-on-surface-variant leading-snug">
                    {row.admissionDate
                      ? new Date(row.admissionDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                      : row.createdDate || '—'}
                  </p>
                </div>

                {/* Column 3: Course/ Batches/ Branch */}
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-on-surface leading-snug">{row.course || '—'}</p>
                  <p className="text-xs text-on-surface-variant leading-snug">{row.batch || '—'}</p>
                  <p className="text-xs text-on-surface-variant leading-snug">{row.institute || row.company || '—'}</p>
                  <p className="text-xs text-on-surface-variant leading-snug">{row.branch || '—'}</p>
                </div>

                {/* Actions */}
                <div className="flex justify-end select-none" onClick={(e) => e.stopPropagation()}>
                  <AppActionMenu
                    triggerLabel=""
                    triggerIcon="more_vert"
                    showChevron={false}
                    triggerClassName="rounded-lg p-1.5 text-outline-variant transition-colors hover:bg-surface-container-low hover:text-primary bg-transparent cursor-pointer"
                    items={[
                      { key: 'edit', label: 'Edit', icon: 'edit' },
                      { key: 'archive', label: row.status === 'Active' ? 'Archive' : 'Unarchive', icon: row.status === 'Active' ? 'archive' : 'unarchive' },
                      { key: 'delete', label: 'Delete', icon: 'delete' },
                      { key: 'attendance', label: 'Attendance', icon: 'fact_check' },
                      { key: 'biometric', label: 'Biometric Records', icon: 'fingerprint' },
                      { key: 'messages', label: 'Messages', icon: 'chat_bubble' },
                      { key: 'receipt', label: 'Receipt', icon: 'receipt_long' },
                      { key: 'ledger', label: 'Ledger', icon: 'table_chart' },
                      { key: 'ledger-installments', label: 'Ledger With Installments', icon: 'payments' },
                      { key: 'remarks', label: 'Remarks', icon: 'sticky_note_2' },
                      { key: 'inventory', label: 'Inventory', icon: 'inventory_2' },
                      { key: 'document', label: 'Document', icon: 'description' },
                      { key: 'performance', label: 'Performance', icon: 'trending_up' },
                      { key: 'report-card', label: 'Report Card', icon: 'school' },
                      { key: 'send-report-email', label: 'Send ReportCard Email', icon: 'mail' },
                      { key: 'admit', label: 'Admit', icon: 'person_add' },
                      { key: 'generate-password', label: 'Generate Password', icon: 'key' },
                    ]}
                    onSelect={(key) => {
                      if (key === 'edit') {
                        navigate(`/admission/students/entry?id=${row.id}&mode=edit`)
                      } else if (key === 'archive') {
                        actions.handleArchive(row.id)
                      } else if (key === 'delete') {
                        actions.handleDelete(row.id)
                      } else if (key === 'attendance') {
                        navigate(`/admission/students/attendance?id=${row.id}`)
                      } else if (key === 'biometric') {
                        navigate(`/admission/students/biometric?id=${row.id}`)
                      } else if (key === 'messages') {
                        navigate(`/admission/students/messages?id=${row.id}`)
                      } else if (key === 'receipt') {
                        navigate(`/admission/students/receipt?id=${row.id}`)
                      } else if (key === 'ledger') {
                        navigate(`/admission/students/ledger?id=${row.id}`)
                      } else if (key === 'ledger-installments') {
                        navigate(`/admission/students/ledger-installments?id=${row.id}`)
                      } else if (key === 'remarks') {
                        navigate(`/admission/students/remarks?id=${row.id}`)
                      } else if (key === 'inventory') {
                        navigate(`/admission/students/inventory?id=${row.id}`)
                      } else if (key === 'document') {
                        navigate(`/admission/students/document?id=${row.id}`)
                      } else if (key === 'performance') {
                        navigate(`/admission/students/performance?id=${row.id}`)
                      } else if (key === 'report-card') {
                        navigate(`/admission/students/report-card?id=${row.id}`)
                      } else if (key === 'send-report-email') {
                        navigate(`/admission/students/send-report?id=${row.id}`)
                      } else if (key === 'admit') {
                        navigate(`/admission/students/entry?copyFrom=${row.id}&mode=admit`)
                      } else if (key === 'generate-password') {
                        navigate(`/admission/students/password?id=${row.id}`)
                      }
                    }}
                  />
                </div>
              </div>
            ))
          )}
        </div>

        {/* ── Pagination Footer (inside table container) ────────────────────── */}
        <div className="flex flex-col gap-3 border-t border-outline-variant/30 bg-surface-container-low/30 px-8 py-5 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-sm text-on-surface-variant">
            Showing {state.rows.length === 0 ? 0 : (state.page - 1) * state.pageSize + 1}–{Math.min(state.page * state.pageSize, state.meta.totalCount)} of {state.meta.totalCount} entries
          </span>
          {state.meta.totalPages > 1 && (
            <AppPagination
              page={state.page}
              totalPages={state.meta.totalPages}
              onPageChange={actions.setPage}
              onPrevious={() => actions.setPage(Math.max(1, state.page - 1))}
              onNext={() => actions.setPage(Math.min(state.meta.totalPages, state.page + 1))}
              canPrevious={state.page > 1}
              canNext={state.page < state.meta.totalPages}
            />
          )}
        </div>
      </div>

      {/* ── Modals ──────────────────────────────────────────────────────────── */}
      <SendSMSModal
        isOpen={activeModal === 'sms'}
        onClose={closeModal}
        students={state.rows}
      />
      <SendEmailModal
        isOpen={activeModal === 'email'}
        onClose={closeModal}
        students={state.rows}
      />
      <BatchTransferModal
        isOpen={activeModal === 'batch-transfer'}
        onClose={closeModal}
        students={state.rows}
        selectedStudentIds={selectedRowIds}
        currentBatch={state.batch}
        onComplete={actions.refresh}
        onSuccess={() => setSelectedRowIds(new Set())}
      />
      <RegenerateRollNoModal
        isOpen={activeModal === 'regenerate-roll-no'}
        onClose={closeModal}
        students={state.rows}
        onComplete={actions.refresh}
      />
      <UpdateColumnsModal
        isOpen={activeModal === 'update-columns'}
        onClose={closeModal}
        students={state.rows}
        onSuccess={actions.refresh}
      />
      <SendReportCardModal
        isOpen={activeModal === 'send-report-card'}
        onClose={closeModal}
        students={state.rows}
      />
      <PrintReportModal
        isOpen={showPrintReport}
        onClose={() => setShowPrintReport(false)}
        students={state.rows}
      />
    </div>
  )
}
